// Local storage for documents that have not reached the server yet.

import { getUserId } from './tokenUtils';

const DB_NAME = 'RNR_DOCUMENT_DRAFTS';
const DB_VERSION = 1;
const DRAFTS = 'drafts';
const IMAGES = 'draftImages';

let dbPromise = null;

const openDb = () => {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      if (!db.objectStoreNames.contains(DRAFTS)) {
        const drafts = db.createObjectStore(DRAFTS, { keyPath: 'draftId' });
        drafts.createIndex('ownerUserId', 'ownerUserId', { unique: false });
        drafts.createIndex('syncState', 'syncState', { unique: false });
        drafts.createIndex('updatedOn', 'updatedOn', { unique: false });
      }

      if (!db.objectStoreNames.contains(IMAGES)) {
        const images = db.createObjectStore(IMAGES, { keyPath: 'imageKey' });
        images.createIndex('draftId', 'draftId', { unique: false });
      }
    };
  });

  return dbPromise;
};

const run = async (storeNames, mode, work) => {
  const db = await openDb();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeNames, mode);
    let result;

    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);

    work(tx, (value) => {
      result = value;
    });
  });
};

const request = (req) =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const currentOwner = () => {
  try {
    const id = getUserId();
    return id ? String(id) : '';
  } catch {
    return '';
  }
};

const ownedByCurrentUser = (draft, owner) => {
  if (!draft) return false;
  const draftOwner = draft.ownerUserId ? String(draft.ownerUserId) : '';
  if (!draftOwner) return false;
  return draftOwner === owner;
};

export const newDraftId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

export const SYNC_LOCAL = 'local';
export const SYNC_PENDING = 'pending';
export const SYNC_SYNCING = 'syncing';
export const SYNC_FAILED = 'failed';

export const putDraft = async (draft) => {
  const now = Date.now();

  const record = {
    syncState: SYNC_LOCAL,
    intent: null,
    documentId: null,
    attemptCount: 0,
    lastError: null,
    ...draft,
    ownerUserId: String(draft.ownerUserId || currentOwner() || ''),
    createdOn: draft.createdOn || now,
    updatedOn: now,
  };

  await run([DRAFTS], 'readwrite', (tx) => {
    tx.objectStore(DRAFTS).put(record);
  });

  return record;
};

export const patchDraft = async (draftId, changes) => {
  const existing = await getDraft(draftId);
  if (!existing) return null;

  return putDraft({ ...existing, ...changes });
};

export const getDraft = async (draftId) => {
  if (!draftId) return null;

  const owner = currentOwner();
  const db = await openDb();
  const tx = db.transaction([DRAFTS], 'readonly');
  const found = await request(tx.objectStore(DRAFTS).get(draftId));

  return ownedByCurrentUser(found, owner) ? found : null;
};

export const getDrafts = async () => {
  const owner = currentOwner();
  const db = await openDb();
  const tx = db.transaction([DRAFTS], 'readonly');
  const all = (await request(tx.objectStore(DRAFTS).getAll())) ?? [];

  return all
    .filter((draft) => ownedByCurrentUser(draft, owner))
    .sort((a, b) => (a.createdOn ?? 0) - (b.createdOn ?? 0));
};

export const deleteDraft = async (draftId) => {
  if (!draftId) return;

  await run([DRAFTS, IMAGES], 'readwrite', (tx) => {
    tx.objectStore(DRAFTS).delete(draftId);

    const index = tx.objectStore(IMAGES).index('draftId');
    index.openKeyCursor(IDBKeyRange.only(draftId)).onsuccess = (event) => {
      const cursor = event.target.result;
      if (!cursor) return;
      tx.objectStore(IMAGES).delete(cursor.primaryKey);
      cursor.continue();
    };
  });
};

const imageKey = (draftId, uid) => `${draftId}:${uid}`;

export const syncDraftImages = async (draftId, entries) => {
  const db = await openDb();
  const tx = db.transaction([IMAGES], 'readwrite');
  const store = tx.objectStore(IMAGES);

  const existingRows =
    (await request(store.index('draftId').getAll(IDBKeyRange.only(draftId)))) ?? [];
  const existingByKey = new Map(existingRows.map((row) => [row.imageKey, row]));

  const wanted = new Set();

  entries.forEach((entry) => {
    if (!entry?.blob) return;

    const key = imageKey(draftId, entry.uid);
    wanted.add(key);

    // Re-store when the photo is new or its blob changed (e.g. the location/time
    // stamp was burned on after the first save), so the stored draft keeps the
    // final tagged image rather than the first version written.
    const prev = existingByKey.get(key);
    if (prev && prev.size === entry.blob.size) return;

    store.put({
      imageKey: key,
      draftId,
      uid: entry.uid,
      fieldKey: String(entry.fieldKey),
      name: entry.name ?? 'photo',
      type: entry.type ?? entry.blob.type ?? 'image/jpeg',
      size: entry.blob.size,
      blob: entry.blob,
    });
  });

  existingByKey.forEach((row, key) => {
    if (!wanted.has(key)) store.delete(key);
  });

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
};

export const getDraftImages = async (draftId) => {
  const db = await openDb();
  const tx = db.transaction([IMAGES], 'readonly');
  const rows = (await request(tx.objectStore(IMAGES).index('draftId').getAll(IDBKeyRange.only(draftId)))) ?? [];

  return rows;
};

export const requestPersistentStorage = async () => {
  try {
    if (!navigator.storage?.persist) return false;
    if (await navigator.storage.persisted?.()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
};
